import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTickets } from "../api/tickets";
import SidebarCalendar from "../components/SidebarCalendar";
import TicketImportWizard from "../components/tickets/TicketImportWizard";
import SBINModal from "../components/modals/SBINModal";
import DateRangeErrorBanner from "../components/common/DateRangeErrorBanner";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useUiModal } from "../context/UiModalContext";
import { useAuth } from "../context/AuthContext";
import { formatDateInTimeZone, parseBackendDateTime } from "../utils/datetime";
import { getSalonTimezone } from "../utils/salonTimezone";
import "./TicketList.css";

const STORAGE_KEY = "sbin:ticketsFilters:v2";

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "What this page shows",
    description: "View ticket receipts and payment totals for the selected date range.",
    anchorId: "tl-overview",
    bullets: [
      "Tickets are split into Open and Closed sections.",
      "Click a client name to open ticket details.",
      "Use this page for daily receipt review and follow-up.",
    ],
    tips: ["Confirm your date range before comparing totals."],
  },
  {
    id: "filters",
    title: "Filters",
    description: "Use date range and ticket/client search to narrow results.",
    anchorId: "tl-filters",
    bullets: [
      "Date From and Date To define the report window.",
      "Search matches ticket number and client name.",
      "Sidebar calendar quickly sets a single day view.",
    ],
    tips: ["If results look empty, clear search and try a wider date range."],
  },
  {
    id: "actions",
    title: "Actions",
    description: "Run searches, create new tickets, and use import tools.",
    anchorId: "tl-actions",
    bullets: [
      "Search refreshes the list with current filters.",
      "New Ticket opens the ticket creation flow.",
      "Import Tickets and Import History support bulk workflows.",
    ],
    tips: ["Use Import History to confirm large uploads completed correctly."],
  },
  {
    id: "open",
    title: "Open tickets table",
    description: "Shows tickets still in progress or not finalized.",
    anchorId: "tl-open",
    bullets: [
      "Sort by Type, Date, Name, or Amount.",
      "Open rows to continue editing and payment updates.",
    ],
    tips: ["Review open tickets daily to avoid missed checkouts."],
  },
  {
    id: "closed",
    title: "Closed tickets table",
    description: "Shows completed, paid, refunded, or finalized tickets.",
    anchorId: "tl-closed",
    bullets: [
      "Use sorting to audit highest amounts or latest closes.",
      "Open rows to inspect full ticket history.",
    ],
    tips: ["Use closed tickets for end-of-day reconciliation checks."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "No tickets shown",
    body: "Check date range and search text, then run Search again.",
  },
  {
    title: "Date range error",
    body: "Date To must be the same as or later than Date From.",
  },
  {
    title: "Missing import results",
    body: "Open Import History to verify import status and row outcomes.",
  },
];

const TOOLTIPS = {
  date_from: "Start date for ticket results in salon timezone.",
  date_to: "End date for ticket results in salon timezone.",
  search: "Search by ticket number or client name.",
  actions: "Run search, create tickets, or open import tools.",
  table_date: "Sort tickets by ticket date.",
  table_amount: "Sort tickets by grand total amount.",
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

function formatDateInput(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const parts = formatter.formatToParts(date);
      const lookup = { year: "", month: "", day: "" };
      for (const part of parts) {
        if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
          lookup[part.type] = part.value;
        }
      }
      if (lookup.year && lookup.month && lookup.day) {
        return `${lookup.year}-${lookup.month}-${lookup.day}`;
      }
    } catch (err) {
      // Fall back to local time when timezone is invalid or unsupported.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftIsoDate(dateValue, dayDelta) {
  if (typeof dateValue !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return "";
  }
  const [year, month, day] = dateValue.split("-").map((part) => Number(part));
  const base = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(base.getTime())) return "";
  base.setUTCDate(base.getUTCDate() + Number(dayDelta || 0));
  const nextYear = base.getUTCFullYear();
  const nextMonth = String(base.getUTCMonth() + 1).padStart(2, "0");
  const nextDay = String(base.getUTCDate()).padStart(2, "0");
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const next = {};
    if (typeof parsed.search === "string") next.search = parsed.search;
    return Object.keys(next).length ? next : null;
  } catch (err) {
    return null;
  }
}

const closedStatusValues = new Set([
  "closed",
  "paid",
  "completed",
  "refunded",
  "void",
]);

function normalizeStatus(value) {
  return (value || "").toLowerCase();
}

function getTicketDateValue(ticket) {
  return (
    ticket?.ticket_date ||
    ticket?.ticket_day ||
    ticket?.ticketDay ||
    ticket?.closed_at ||
    ticket?.closedAt ||
    ticket?.opened_at ||
    ticket?.openedAt ||
    ticket?.created_at ||
    ticket?.createdAt ||
    ticket?.date ||
    null
  );
}

function getTicketDateKey(ticket) {
  const raw =
    ticket?.ticket_date ||
    ticket?.ticket_day ||
    ticket?.ticketDay ||
    null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  return null;
}

function ticketMatchesFilters(ticket, filters) {
  if (!ticket) return false;
  if (filters.dateFrom || filters.dateTo) {
    const ticketDateKey = getTicketDateKey(ticket);
    if (!ticketDateKey) return false;
    if (filters.dateFrom && ticketDateKey < filters.dateFrom) return false;
    if (filters.dateTo && ticketDateKey > filters.dateTo) return false;
  }
  const search = (filters.search || "").trim().toLowerCase();
  if (search) {
    const ticketNumber = (ticket.ticket_number || "").toLowerCase();
    const clientName = (ticket.client?.full_name || "").toLowerCase();
    if (!ticketNumber.includes(search) && !clientName.includes(search)) {
      return false;
    }
  }
  return true;
}

function getTicketTypeLabel(ticket) {
  if (!ticket) return "Sales";
  if (ticket.is_refund === true) return "Refund";
  const rawType =
    ticket.ticket_type ||
    ticket.type ||
    ticket.kind ||
    ticket.ticketType ||
    ticket.ticketKind;
  const normalized = (rawType || "").toString().toLowerCase();
  if (["payment_in"].includes(normalized)) return "Payment In";
  if (["payment_out"].includes(normalized)) return "Payment Out";
  if (["refund"].includes(normalized)) return "Refund";
  if (["sale", "sales"].includes(normalized)) return "Sales";
  if (typeof ticket.grand_total === "number" && ticket.grand_total < 0) {
    return "Refund";
  }
  return "Sales";
}

function formatTicketDate(value, timeZone) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${month}/${day}/${year}`;
  }
  return formatDateInTimeZone(value, timeZone);
}

function parseAmountValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const isNegative = raw.includes("-") || (raw.startsWith("(") && raw.endsWith(")"));
  const cleaned = raw.replace(/[^\d.]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  if (Number.isNaN(parsed)) return null;
  return isNegative ? -parsed : parsed;
}

function getTicketDateSortValue(ticket) {
  const dateKey = getTicketDateKey(ticket);
  if (dateKey) {
    const [year, month, day] = dateKey.split("-").map((part) => Number(part));
    if ([year, month, day].every((part) => Number.isFinite(part))) {
      return Date.UTC(year, month - 1, day);
    }
  }
  const rawValue = getTicketDateValue(ticket);
  const parsed = parseBackendDateTime(rawValue);
  if (!parsed) return null;
  return parsed.getTime();
}

function compareTicketValues(a, b, key) {
  if (key === "date") {
    const aValue = getTicketDateSortValue(a);
    const bValue = getTicketDateSortValue(b);
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;
    return aValue - bValue;
  }
  if (key === "amount") {
    const aValue = parseAmountValue(a?.grand_total);
    const bValue = parseAmountValue(b?.grand_total);
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;
    return aValue - bValue;
  }
  const aText =
    key === "type"
      ? getTicketTypeLabel(a)
      : (a?.client?.full_name || "").trim();
  const bText =
    key === "type"
      ? getTicketTypeLabel(b)
      : (b?.client?.full_name || "").trim();
  return aText.localeCompare(bText, undefined, { sensitivity: "base" });
}

function sortTicketsList(list, sortConfig) {
  if (!Array.isArray(list)) return [];
  const sortKey = sortConfig?.key || "date";
  const direction = sortConfig?.direction === "asc" ? 1 : -1;
  return list
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const compare = compareTicketValues(left.item, right.item, sortKey);
      if (compare !== 0) return compare * direction;
      return left.index - right.index;
    })
    .map((entry) => entry.item);
}

function getNextSortConfig(prev, key) {
  if (prev?.key === key) {
    return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
  }
  const defaultDirection = key === "date" || key === "amount" ? "desc" : "asc";
  return { key, direction: defaultDirection };
}

export default function TicketList() {
  const navigate = useNavigate();
  const { openNewTicketModal } = useUiModal();
  const { currentSalon, user, token } = useAuth();
  const fallbackSalonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const effectiveSalonId = currentSalon?.id ?? fallbackSalonId ?? null;
  const storedFilters = loadStoredFilters();
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateRangeInvalid, setDateRangeInvalid] = useState(false);
  const today = useMemo(
    () => formatDateInput(new Date(), salonTimezone),
    [salonTimezone]
  );
  const [search, setSearch] = useState(storedFilters?.search || "");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showSalonRequiredModal, setShowSalonRequiredModal] = useState(false);
  const [openSort, setOpenSort] = useState({ key: "date", direction: "desc" });
  const [closedSort, setClosedSort] = useState({ key: "date", direction: "desc" });
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);
  const selectedDate = dateFrom ? new Date(`${dateFrom}T00:00:00`) : new Date();

  useEffect(() => {
    if (!token) {
      navigate("/login?next=/tickets");
    }
  }, [token, navigate]);

  useEffect(() => {
    try {
      localStorage.removeItem("sbin:ticketsFilters:v1");
    } catch (err) {
      // Ignore storage failures (private mode or blocked storage).
    }
  }, []);

  useEffect(() => {
    setDateFrom(today);
    setDateTo(today);
  }, [today]);

  useEffect(() => {
    if (effectiveSalonId) {
      setShowSalonRequiredModal(false);
    }
  }, [effectiveSalonId]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const jumped = scrollToSection(id);
      if (!jumped) return;
      setHighlightedSectionId(id);
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId((current) => (current === id ? "" : current));
      }, 1500);
    },
    [scrollToSection]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const fetchTickets = async () => {
    if (user?.is_super_admin && !effectiveSalonId) {
      setTickets([]);
      setError("");
      setShowSalonRequiredModal(true);
      return;
    }
    if (isInvalidDateRange(dateFrom, dateTo)) {
      setDateRangeInvalid(true);
      setError("");
      return;
    }
    setDateRangeInvalid(false);
    setLoading(true);
    setError("");
    try {
      const params = {};
      // Pad the backend query by a day on each side, then apply the exact
      // accounting-date filter locally. This keeps just-closed tickets visible
      // when backend indexing lags behind the displayed ticket date.
      if (dateFrom) params.date_from = shiftIsoDate(dateFrom, -1) || dateFrom;
      if (dateTo) params.date_to = shiftIsoDate(dateTo, 1) || dateTo;
      if (search.trim()) params.client_search = search.trim();

      const data = await getTickets(params);
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 400 &&
        String(detail || "").includes("INVALID_DATE_RANGE")
      ) {
        setDateRangeInvalid(true);
        setError("");
        return;
      }
      if (
        user?.is_super_admin &&
        (String(detail || "").includes("Select a salon first") ||
          String(detail || "").includes("SALON_CONTEXT_MISMATCH"))
      ) {
        setTickets([]);
        setError("");
        setShowSalonRequiredModal(true);
        return;
      }
      console.error(err);
      setError(
        err?.message || "Unable to load tickets. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const handleTicketCreated = (event) => {
      const createdTicket = event?.detail;
      if (
        !ticketMatchesFilters(createdTicket, {
          dateFrom,
          dateTo,
          search,
        })
      ) {
        return;
      }
      setTickets((prev) => {
        if (!createdTicket?.id) return prev;
        if (prev.some((ticket) => ticket.id === createdTicket.id)) return prev;
        return [createdTicket, ...prev];
      });
    };

    window.addEventListener("tickets:created", handleTicketCreated);
    return () => {
      window.removeEventListener("tickets:created", handleTicketCreated);
    };
  }, [dateFrom, dateTo, search]);

  useEffect(() => {
    const handleTicketUpdated = () => {
      if (token) {
        fetchTickets();
      }
    };
    window.addEventListener("tickets:updated", handleTicketUpdated);
    window.addEventListener("sbin:appointmentsChanged", handleTicketUpdated);
    window.addEventListener("sbin:salon-moved", handleTicketUpdated);
    return () => {
      window.removeEventListener("tickets:updated", handleTicketUpdated);
      window.removeEventListener("sbin:appointmentsChanged", handleTicketUpdated);
      window.removeEventListener("sbin:salon-moved", handleTicketUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, search, token]);

  useEffect(() => {
    try {
      const payload = {
        search,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // Ignore storage failures (private mode or blocked storage).
    }
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (isInvalidDateRange(dateFrom, dateTo)) {
      setDateRangeInvalid(true);
      setError("");
      return;
    }
    setDateRangeInvalid(false);
    fetchTickets();
  };

  const handleCalendarChange = (date) => {
    const nextValue = formatDateInput(date, salonTimezone);
    setDateFrom(nextValue);
    setDateTo(nextValue);
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) =>
      ticketMatchesFilters(ticket, { dateFrom, dateTo, search })
    );
  }, [tickets, dateFrom, dateTo, search]);

  const openTicketsRaw = useMemo(() => {
    return filteredTickets.filter(
      (ticket) => !closedStatusValues.has(normalizeStatus(ticket.status))
    );
  }, [filteredTickets]);
  const closedTicketsRaw = useMemo(() => {
    return filteredTickets.filter((ticket) =>
      closedStatusValues.has(normalizeStatus(ticket.status))
    );
  }, [filteredTickets]);
  const openTickets = useMemo(
    () => sortTicketsList(openTicketsRaw, openSort),
    [openTicketsRaw, openSort]
  );
  const closedTickets = useMemo(
    () => sortTicketsList(closedTicketsRaw, closedSort),
    [closedTicketsRaw, closedSort]
  );
  const showSplitView = true;
  const modalText = "text-base md:text-lg leading-normal";
  const labelText =
    "text-sm md:text-base font-semibold tracking-wide text-slate-500";
  const fieldClass =
    "h-11 md:h-12 px-4 text-base md:text-lg rounded-xl border border-slate-300 bg-white leading-normal";
  const btnBase =
    "h-11 md:h-12 px-4 text-base md:text-lg rounded-full font-semibold leading-normal";

  const handleViewTicket = (ticket) => {
    if (!ticket?.id) return;
    navigate(`/tickets/${ticket.id}`);
  };

  const handleOpenSort = (key) => {
    setOpenSort((prev) => getNextSortConfig(prev, key));
  };

  const handleClosedSort = (key) => {
    setClosedSort((prev) => getNextSortConfig(prev, key));
  };

  const renderSortIndicator = (sortConfig, key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return (
      <span className="text-[10px] leading-none">
        {sortConfig.direction === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  const renderTicketsTable = (title, list, sortConfig, onSort) => (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden min-w-0">
      <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between bg-slate-50">
        <h2 className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
          {title} ({list.length})
        </h2>
      </div>
      <div className="w-full">
        <table className="w-full table-fixed text-[13px] leading-normal">
          <colgroup>
            <col style={{ width: "28%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "35%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead className="bg-white">
            <tr>
              <th className="px-4 py-2.5 text-left whitespace-nowrap text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                <button
                  type="button"
                  className="inline-flex items-center gap-1"
                  onClick={() => onSort("type")}
                >
                  <span>Type</span>
                  {renderSortIndicator(sortConfig, "type")}
                </button>
              </th>
              <th className="px-4 py-2.5 text-left whitespace-nowrap text-[11px] font-semibold tracking-wide text-slate-500 uppercase min-w-[110px]">
                <button
                  type="button"
                  className="inline-flex items-center gap-1"
                  onClick={() => onSort("date")}
                >
                  <span>Date</span>
                  <InfoTooltip text={TOOLTIPS.table_date} label="Date" />
                  {renderSortIndicator(sortConfig, "date")}
                </button>
              </th>
              <th className="px-4 py-2.5 text-left whitespace-nowrap text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                <button
                  type="button"
                  className="inline-flex items-center gap-1"
                  onClick={() => onSort("name")}
                >
                  <span>Name</span>
                  {renderSortIndicator(sortConfig, "name")}
                </button>
              </th>
              <th className="px-4 py-2.5 text-right whitespace-nowrap text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 justify-end w-full"
                  onClick={() => onSort("amount")}
                >
                  <span>Amount</span>
                  <InfoTooltip text={TOOLTIPS.table_amount} label="Amount" />
                  {renderSortIndicator(sortConfig, "amount")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-4 text-center text-[13px] text-slate-500 whitespace-nowrap"
                >
                  No tickets.
                </td>
              </tr>
            )}
            {list.map((t) => {
              const fullName = t.client?.full_name || "View ticket";
              const ticketDate = getTicketDateValue(t);
              const accountingDate =
                t?.ticket_date || t?.ticket_day || t?.ticketDay || null;
              const ticketTimezone = t?.salon_timezone || salonTimezone;
              const dateLabel = formatTicketDate(
                accountingDate || ticketDate,
                ticketTimezone
              );
              return (
                <tr
                  key={t.id}
                  className={`border-t border-slate-100 ${
                    normalizeStatus(t.status) === "closed"
                      ? "bg-emerald-50 text-slate-700"
                      : "hover:bg-slate-50"
                  }`}
                >
            <td className="px-4 py-2.5 text-[13px] text-slate-700 truncate">
              {getTicketTypeLabel(t)}
            </td>
            <td className="px-4 py-2.5 text-[13px] text-slate-700 truncate min-w-[110px]">
              {dateLabel}
            </td>
            <td className="px-4 py-2.5 text-[13px] text-slate-700 truncate">
              <button
                type="button"
                className="block w-full truncate font-semibold text-slate-900 hover:underline text-left"
                onClick={() => handleViewTicket(t)}
                title={fullName}
              >
                {fullName}
              </button>
              <div className="text-[11px] text-slate-500">
                Ticket #
                {t.ticket_number || t.external_ticket_number || t.id}
              </div>
              {t.main_staff_name ? (
                <div className="text-[11px] text-slate-500">
                  Staff: {t.main_staff_name}
                </div>
              ) : null}
            </td>
            <td className="px-4 py-2.5 text-[13px] text-slate-700 whitespace-nowrap text-right">
              ${t.grand_total}
            </td>
          </tr>
          );
            })}
        </tbody>
      </table>
    </div>
    </div>
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="ticketsPageWide tickets-page-compact">
      <div className="flex flex-col lg:flex-row gap-6">
        <SidebarCalendar
          selectedDate={selectedDate}
          onChange={handleCalendarChange}
          showOnMobile
        />
        <div className="flex-1 min-w-0">
          <div
            className={`w-full rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden ${modalText}`}
          >
            <div className="bg-rose-500 px-6 py-4 relative">
              <h1 className="text-xl md:text-2xl font-semibold text-white">
                Tickets
              </h1>
              <p className="text-sm md:text-base text-rose-100">
                View visit receipts and payment history.
              </p>
              <div
                className="absolute right-4 top-3 z-50"
                onClick={handleClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <ModalCloseX onClick={handleClose} />
              </div>
            </div>

            <div className="p-6 space-y-6">
              <DateRangeErrorBanner show={dateRangeInvalid} />
              {error && !dateRangeInvalid && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div
                id="tl-overview"
                className={getSectionClassName("tl-overview", "")}
              >
                <ReportHelpCard
                  title="Help Guide: Tickets"
                  subtitle="View ticket receipts, filter by date, and manage imports from one page."
                  sections={HELP_SECTIONS}
                  troubleshooting={TROUBLESHOOTING_ITEMS}
                  onJumpToSection={jumpToSectionWithHighlight}
                />
              </div>

              <form
                id="tl-filters"
                onSubmit={handleSearch}
                className={getSectionClassName("tl-filters", "space-y-4")}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  <div className="flex flex-col gap-1">
                    <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                      Date from
                      <InfoTooltip text={TOOLTIPS.date_from} label="Date from" />
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={`${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                      Date to
                      <InfoTooltip text={TOOLTIPS.date_to} label="Date to" />
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={`${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
                    />
                  </div>
                  <div className="flex flex-col gap-1 lg:col-span-2">
                    <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                      Search (ticket #, client)
                      <InfoTooltip text={TOOLTIPS.search} label="Search" />
                    </label>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className={`${fieldClass} focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10`}
                      placeholder="e.g. T-2024..., Jane, 555"
                    />
                  </div>
                  <div className="flex flex-col gap-1 min-w-[160px]">
                    <label className={`${labelText} uppercase inline-flex items-center gap-2`}>
                      Actions
                      <InfoTooltip text={TOOLTIPS.actions} label="Actions" />
                    </label>
                    <div
                      id="tl-actions"
                      className={getSectionClassName("tl-actions", "ticketsActions")}
                    >
                      <div className="ticketsActionsRow">
                        <button
                          type="submit"
                          className={`${btnBase} whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50`}
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          onClick={() => openNewTicketModal({ defaultDate: selectedDate })}
                          className={`${btnBase} whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800`}
                        >
                          New Ticket
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsImportOpen(true)}
                          className={`${btnBase} whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50`}
                        >
                          Import Tickets
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/tickets/imports')}
                          className={`${btnBase} whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50`}
                        >
                          Import History
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>

              {loading ? (
                <div className="text-sm text-slate-600">Loading tickets...</div>
              ) : (
                <div
                  className={
                    showSplitView ? 'ticketTablesGrid' : 'grid grid-cols-1 gap-4'
                  }
                >
                  {showSplitView && (
                    <>
                      <div
                        id="tl-open"
                        className={getSectionClassName('tl-open', '')}
                      >
                        {renderTicketsTable(
                          'Open Tickets',
                          openTickets,
                          openSort,
                          handleOpenSort
                        )}
                      </div>
                      <div
                        id="tl-closed"
                        className={getSectionClassName('tl-closed', '')}
                      >
                        {renderTicketsTable(
                          'Closed Tickets',
                          closedTickets,
                          closedSort,
                          handleClosedSort
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <SBINModal
        isOpen={showSalonRequiredModal}
        onClose={() => setShowSalonRequiredModal(false)}
        title="Select a salon first"
        size="sm"
        fitContent={true}
        closeOnBackdrop={true}
        closeOnEscape={true}
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowSalonRequiredModal(false)}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => navigate('/superadmin')}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
            >
              Go to Super Admin Console
            </button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700">
          <p>Select a salon first to view tickets.</p>
          <p>Use the Super Admin Console to switch to a salon context.</p>
        </div>
      </SBINModal>
      <SBINModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        title="Import Tickets"
        size="xxl"
      >
        <TicketImportWizard
          onComplete={() => {
            fetchTickets();
          }}
          onClose={() => setIsImportOpen(false)}
        />
      </SBINModal>
    </div>
  );
}
